import { RequestRatingEnum } from "@nlc/shared-types";

import { StarRatingDisplay } from "@/components/star-rating";

import { ProgressBarView } from "./ProgressBarView";

interface RatingCardProps<TRating extends RequestRatingEnum> {
  count: number;
  label?: string;
  onSelect: (rating: TRating) => void;
  percentage: number;
  rating: TRating;
  selectedRating: null | RequestRatingEnum;
}

const ratingConfig = {
  [RequestRatingEnum.GOOD]: {
    borderColor: "border-utility-success-500",
    hoverBorder: "hover:border-utility-success-500",
    label: "5 stars",
    progressColor: "bg-utility-success-500",
    stars: 5,
  },
  [RequestRatingEnum.NEUTRAL]: {
    borderColor: "border-utility-warning-500",
    hoverBorder: "hover:border-utility-warning-500",
    label: "3 to 4 stars",
    progressColor: "bg-utility-warning-500",
    stars: 3,
  },
  [RequestRatingEnum.POOR]: {
    borderColor: "border-utility-error-500",
    hoverBorder: "hover:border-utility-error-500",
    label: "1 to 2 stars",
    progressColor: "bg-utility-error-500",
    stars: 1,
  },
};

export function RatingCard<TRating extends RequestRatingEnum>({
  count,
  label,
  onSelect,
  percentage,
  rating,
  selectedRating,
}: RatingCardProps<TRating>) {
  const config = ratingConfig[rating];
  const isSelected = selectedRating === rating;

  return (
    <div
      className={`border rounded-md py-5 px-5 h-48 ml-2 flex flex-col justify-between cursor-pointer transition-colors ${config.hoverBorder} ${
        isSelected ? `border-2 ${config.borderColor}` : "border-border-primary"
      }`}
      onClick={() => onSelect(rating)}
    >
      <div className="flex justify-between">
        <StarRatingDisplay size={20} value={config.stars} />
        <div className="flex flex-col items-end text-text-primary">
          <p className="text-3xl font-semibold">{count}</p>
          <p className="text-base">requests</p>
        </div>
      </div>
      <div>
        <p className="text-base font-medium text-text-primary">
          {label ?? config.label}
        </p>
        <ProgressBarView color={config.progressColor} value={percentage} />
        <p className="text-text-secondary">{percentage ?? 0}% of total</p>
      </div>
    </div>
  );
}
